/**
 * Communication Analyser Page
 * Input screen for analysing communications with SSE streaming
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { AnalysisProgress } from '@/components/analysis/AnalysisProgress';
import { createSSEConnection } from '@/utils/sse';
import type { SSEEvent } from '@/utils/sse';

// LocalStorage key for draft
const DRAFT_KEY = 'analyser_draft';

const Analyser: React.FC = () => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [context, setContext] = useState('general');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<string>('');
  const [analysisMessage, setAnalysisMessage] = useState<string>('');
  const [analysisError, setAnalysisError] = useState<string>('');
  
  // Accumulate analysis data from SSE events using ref for latest value
  const analysisIdRef = useRef<string>('');
  const analysisDataRef = useRef<any>({
    sentiment: null,
    emotions: null,
    traits: null,
    clarity: null,
    explanation: null,
  });
  
  const abortRef = useRef<(() => void) | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Context options (from onboarding tags + defaults)
  const contextOptions = [
    { value: 'general', label: 'General Communication' },
    { value: 'team', label: 'Team Member' },
    { value: 'client', label: 'Client' },
    { value: 'manager', label: 'Manager' },
    { value: 'stakeholder', label: 'Stakeholder' },
    { value: 'colleague', label: 'Colleague' },
  ];

  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      try {
        const { text: savedText, context: savedContext } = JSON.parse(draft);
        setText(savedText || '');
        setContext(savedContext || 'general');
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);

  // Auto-save draft to localStorage
  useEffect(() => {
    if (text || context !== 'general') {
      const draft = { text, context, savedAt: new Date().toISOString() };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    }
  }, [text, context]);

  // Note: Auto-expand disabled to prevent layout issues
  // Textarea will scroll instead

  const handleClear = () => {
    if (confirm('Are you sure you want to clear your message? This cannot be undone.')) {
      setText('');
      setContext('general');
      localStorage.removeItem(DRAFT_KEY);
    }
  };

  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setText(clipboardText);
    } catch (err) {
      alert('Failed to read from clipboard. Please paste manually.');
      console.error('Clipboard error:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!text.trim()) {
      alert('Please enter some text to analyse.');
      return;
    }

    // Reset state
    setIsAnalyzing(true);
    setAnalysisStatus('');
    setAnalysisMessage('');
    setAnalysisError('');
    analysisIdRef.current = '';
    analysisDataRef.current = {
      originalText: text, // Store the original message
      context: context,
      sentiment: null,
      emotions: null,
      traits: null,
      clarity: null,
      explanation: null,
    };

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      
      // Fetch CSRF token first
      const csrfResponse = await fetch(`${apiBaseUrl}/lite/csrf_token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrf_token;

      const url = `${apiBaseUrl}/lite/comms_checker`;

      // Create SSE connection with CSRF token
      const abortFn = await createSSEConnection({
        url,
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        body: {
          response: text,
          context: context,
        },
        onEvent: (event: SSEEvent) => {
          console.log('SSE Event:', event);

          // Update status
          setAnalysisStatus(event.status);

          // Update message
          if (event.message) {
            setAnalysisMessage(event.message);
          }

          // Handle error
          if (event.status === 'error') {
            setAnalysisError(event.error || 'An error occurred during analysis');
            setIsAnalyzing(false);
          }

          // Accumulate analysis data from events
          if (event.status === 'activity_processed' && event.result) {
            console.log('📦 Activity processed result:', event.result);
            // Extract lite_doc_id
            if (event.result.lite_doc_id) {
              analysisIdRef.current = event.result.lite_doc_id;
              console.log('✅ Analysis ID set:', event.result.lite_doc_id);
            }
            // Store sentiment and emotions
            if (event.result.result?.analysis) {
              analysisDataRef.current = {
                ...analysisDataRef.current,
                sentiment: event.result.result.analysis.sentiment,
                emotions: event.result.result.analysis.emotion,
              };
              console.log('📊 Sentiment & emotions stored');
            }
          }

          if (event.status === 'traits_received' && event.result) {
            console.log('🎯 Traits received:', event.result.traits);
            analysisDataRef.current = {
              ...analysisDataRef.current,
              traits: event.result.traits,
            };
          }

          if (event.status === 'clarity_analyzed' && event.result) {
            console.log('✨ Clarity analysed:', event.result);
            analysisDataRef.current = {
              ...analysisDataRef.current,
              clarity: event.result,
            };
          }

          if (event.status === 'explanation_received' && event.result) {
            console.log('💡 Explanation received');
            analysisDataRef.current = {
              ...analysisDataRef.current,
              explanation: event.result.explanation,
            };
          }

          // Handle completion - navigate with accumulated data
          if (event.status === 'complete') {
            console.log('🎉 Analysis complete!');
            console.log('Analysis ID:', analysisIdRef.current);
            console.log('Accumulated data:', analysisDataRef.current);
            
            if (analysisIdRef.current) {
              // Clear the draft since analysis is complete
              localStorage.removeItem(DRAFT_KEY);
              
              setTimeout(() => {
                // Pass accumulated data via navigation state
                console.log('🚀 Navigating to:', `/analysis/${analysisIdRef.current}`);
                navigate(`/analysis/${analysisIdRef.current}`, {
                  state: analysisDataRef.current,
                });
              }, 1500);
            } else {
              console.error('❌ No analysis ID found');
              setAnalysisError('Analysis completed but no ID was returned');
              setIsAnalyzing(false);
            }
          }
        },
        onError: (error: Error) => {
          console.error('SSE Error:', error);
          // Provide user-friendly error message
          const userMessage = error.message.includes('Failed to fetch') 
            ? 'Connection lost during analysis. Please check your internet and try again.'
            : error.message.includes('timeout')
            ? 'Analysis took too long. Please try again with a shorter message.'
            : error.message || 'An error occurred during analysis. Please try again.';
          setAnalysisError(userMessage);
          setIsAnalyzing(false);
        },
        onComplete: () => {
          console.log('SSE connection closed');
          // Don't set isAnalyzing to false here - wait for complete/error status
        },
      });

      abortRef.current = abortFn;
    } catch (error) {
      console.error('Failed to start analysis:', error);
      setAnalysisError(error instanceof Error ? error.message : 'Failed to start analysis');
      setIsAnalyzing(false);
    }
  };

  const handleCancel = () => {
    if (abortRef.current) {
      abortRef.current();
    }
    setIsAnalyzing(false);
    setAnalysisStatus('');
    setAnalysisMessage('');
    setAnalysisError('');
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 sm:mb-8 px-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            Communication Analyser
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600">
            Analyse your messages for sentiment, emotion, clarity, and behavioural traits
          </p>
        </div>

        {!isAnalyzing ? (
          /* Input Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Context Dropdown */}
            <div>
              <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
                Audience Context
              </label>
              <select
                id="context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {contextOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message Textarea */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Your Message
                </label>
                <span className={`text-xs font-medium ${
                  text.length === 0 ? 'text-gray-500' :
                  text.length < 10 ? 'text-amber-600' :
                  text.length > 10000 ? 'text-red-600' :
                  'text-green-600'
                }`}>
                  {text.length} / 10,000 characters
                  {text.length > 0 && text.length < 10 && ' (min 10)'}
                  {text.length > 10000 && ' (max exceeded!)'}
                </span>
              </div>
              <textarea
                ref={textareaRef}
                id="message"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste or type your message here to analyse... (minimum 10 characters)"
                className={`w-full h-64 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none overflow-y-auto ${
                  text.length > 10000 ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                maxLength={10000}
              />
              {text.length > 0 && text.length < 10 && (
                <p className="text-xs text-amber-600 mt-1">
                  Please enter at least 10 characters for an analysis
                </p>
              )}
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleClear}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-lg sm:text-xl mr-1 sm:mr-2">
                    delete
                  </span>
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handlePaste}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-lg sm:text-xl mr-1 sm:mr-2">
                    content_paste
                  </span>
                  Paste
                </button>
              </div>

              <button
                type="submit"
                disabled={text.trim().length < 10}
                className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base !bg-orange-500 !text-white rounded-lg hover:!bg-orange-600 transition-colors font-medium disabled:!bg-gray-300 disabled:!text-gray-700 disabled:cursor-not-allowed flex-shrink-0"
                title={text.trim().length < 10 ? 'Enter at least 10 characters to analyse' : 'Analyse your message'}
              >
                <span className="material-symbols-outlined text-lg sm:text-xl mr-1 sm:mr-2">
                  psychology
                </span>
                Analyse
              </button>
            </div>
          </form>
        ) : (
          /* Analysis Progress */
          <div className="space-y-6">
            <AnalysisProgress
              currentStatus={analysisStatus}
              currentMessage={analysisMessage}
              error={analysisError}
            />

            {/* Cancel Button */}
            {!analysisError && analysisStatus !== 'complete' && (
              <div className="text-center">
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel Analysis
                </button>
              </div>
            )}

            {/* Retry Button on Error */}
            {analysisError && (
              <div className="text-center">
                <button
                  onClick={() => {
                    setIsAnalyzing(false);
                    setAnalysisError('');
                  }}
                  className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
                >
                  <span className="material-symbols-outlined text-xl mr-2">
                    refresh
                  </span>
                  Try Again
                </button>
              </div>
            )}
          </div>
        )}

        {/* Preview Cards (future enhancement) */}
        {!isAnalyzing && text.length > 50 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <span className="material-symbols-outlined text-blue-600 text-2xl sm:text-3xl mb-2">
                sentiment_satisfied
              </span>
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">Sentiment Analysis</h3>
              <p className="text-xs text-gray-600">
                Understand the emotional tone of your message
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <span className="material-symbols-outlined text-purple-600 text-2xl sm:text-3xl mb-2">
                visibility
              </span>
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">Clarity Check</h3>
              <p className="text-xs text-gray-600">
                Ensure your message is clear and understandable
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
              <span className="material-symbols-outlined text-orange-600 text-2xl sm:text-3xl mb-2">
                psychology
              </span>
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">Behavioral Traits</h3>
              <p className="text-xs text-gray-600">
                Identify communication patterns and traits
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analyser;
